/**
 * Precision CAP Portal — sign-in screen for invited FDR contacts.
 *
 * This is invitation-only. There is no "register" link. If a vendor has not
 * been invited yet, the redeem flow at /redeem/:token is how they get an
 * account; everything else lives behind email + password + MFA.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';

const API = process.env.REACT_APP_API_URL || '';

export default function PortalLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || 'We could not sign you in. Check your email and password.');
        setLoading(false);
        return;
      }

      // (a) MFA challenge — user already has MFA enabled.
      if (data.mfa_required && data.challenge_token) {
        sessionStorage.setItem('portal_challenge_token', data.challenge_token);
        navigate('/mfa/challenge', { state: { challenge_token: data.challenge_token } });
        return;
      }

      // (b) MFA enrollment required — first time signing in.
      if (data.mfa_enrollment_required && data.enroll_token) {
        sessionStorage.setItem('portal_enroll_token', data.enroll_token);
        navigate('/mfa/enroll', { state: { enroll_token: data.enroll_token } });
        return;
      }

      // Anything else means a session was created and the user can proceed.
      navigate('/cap');
    } catch (err) {
      setError('We could not reach the server. Please try again.');
      setLoading(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-md mx-auto">
        <h1 className="text-[28px] font-semibold text-slate-900 leading-tight">
          Sign in
        </h1>
        <p className="text-slate-600 mt-2 text-[15px]">
          Sign in to view your assigned corrective action plan.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 bg-white ring-1 ring-slate-200 rounded-xl p-6 space-y-5">
          <div>
            <label htmlFor="portal-email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="portal-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="portal-password" className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              id="portal-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email || !password}
            className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white transition ${
              loading || !email || !password
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-slate-900 hover:bg-slate-800'
            }`}
          >
            {loading ? 'Signing you in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-[13px] text-slate-500 mt-6">
          Access to this portal is by invitation. If you were sent a link to set up your
          account and have not used it yet, open that link to finish setup.
        </p>
      </div>
    </PortalLayout>
  );
}
